import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Store, Search, Filter, Download, Lock, CheckCircle, 
  ArrowRight, Code2, Zap, Layers, Star, X, Terminal,
  Cpu, LayoutTemplate, Rocket, ShieldCheck, ExternalLink
} from 'lucide-react';
import { db, auth } from '../firebase';
import { collection, query, getDocs, addDoc, serverTimestamp, orderBy } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { YachtLogo } from '../components/YachtLogo';
import Navbar from '../components/Navbar';

// --- SEED DATA ---
const DEFAULT_MARKETPLACE_ITEMS = [
  {
    id: 'item-1',
    title: 'SaaS Alpha Engine',
    description: 'A complete multi-tenant SaaS architecture with robust authentication, role-based access, Stripe billing, and a real-time analytic dashboard.',
    category: 'Template',
    price: 0,
    author: 'Yacht Labs',
    metrics: { installs: 3420, revenue: 0, engagement: 98 },
    files: {
      'index.html': `<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>SaaS Alpha Engine</title>
    <script src="https://cdn.tailwindcss.com"></script>
    <link href="https://fonts.googleapis.com/css2?family=Inter:wght@400;700&family=JetBrains+Mono&display=swap" rel="stylesheet">
    <style>
        body { font-family: 'Inter', sans-serif; }
        .mono { font-family: 'JetBrains Mono', monospace; }
    </style>
</head>
<body class="bg-[#050505] text-white">
    <nav class="p-4 md:p-8 border-b border-white/5 flex justify-between items-center bg-black/40 backdrop-blur-xl sticky top-0 z-50">
        <div class="flex items-center gap-3">
            <div class="w-8 h-8 bg-blue-600 rounded flex items-center justify-center font-bold">A</div>
            <div class="font-bold text-xl tracking-tighter uppercase">Alpha<span class="text-blue-500 font-mono">_SaaS</span></div>
        </div>
        <div class="hidden md:flex items-center gap-8 text-[10px] font-mono uppercase tracking-widest text-zinc-500">
            <a href="#" class="hover:text-white transition-colors">Platform</a>
            <a href="#" class="hover:text-white transition-colors">Pricing</a>
            <a href="#" class="hover:text-white transition-colors">Security</a>
        </div>
        <div class="flex items-center gap-4">
            <button class="text-[10px] font-mono uppercase tracking-widest text-zinc-400 hover:text-white px-4">Login</button>
            <button class="bg-blue-600 px-6 py-2.5 rounded-xl text-[10px] font-mono uppercase tracking-widest font-bold hover:bg-blue-700 transition-all shadow-[0_0_20px_rgba(37,99,235,0.3)]">Get Started</button>
        </div>
    </nav>
    
    <main class="max-w-6xl mx-auto py-24 px-8">
        <div class="max-w-3xl mb-20">
            <div class="inline-block px-3 py-1 bg-blue-500/10 border border-blue-500/20 rounded-full text-[10px] font-mono text-blue-500 uppercase tracking-widest mb-6">v2.4 Production Ready</div>
            <h1 class="text-7xl font-bold tracking-tighter leading-[0.9] mb-8">Build the future of <span class="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-600">software.</span></h1>
            <p class="text-zinc-500 text-xl leading-relaxed mb-10 max-w-xl">A battle-tested foundation for high-performance SaaS applications. Synthesized for modern tech stacks.</p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div class="p-8 bg-zinc-900/30 border border-zinc-800 rounded-[32px] group hover:border-blue-500/50 transition-all">
                <div class="w-12 h-12 bg-blue-500/10 rounded-2xl flex items-center justify-center mb-6 text-blue-500">
                    <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/></svg>
                </div>
                <h3 class="text-xl font-bold mb-3">Secure Auth</h3>
                <p class="text-zinc-500 text-sm leading-relaxed">Integrated Firebase/NextAuth with MFA and multi-tenant support.</p>
            </div>
            <div class="p-8 bg-zinc-900/30 border border-zinc-800 rounded-[32px] group hover:border-blue-500/50 transition-all">
                <div class="w-12 h-12 bg-blue-500/10 rounded-2xl flex items-center justify-center mb-6 text-blue-500">
                    <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M7 15h1m4 0h1m4 0h1m-7 4h12a2 2 0 002-2V5a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>
                </div>
                <h3 class="text-xl font-bold mb-3">Global Payments</h3>
                <p class="text-zinc-500 text-sm leading-relaxed">Stripe Checkout and Customer Portal pre-configured for recurring billing.</p>
            </div>
            <div class="p-8 bg-zinc-900/30 border border-zinc-800 rounded-[32px] group hover:border-blue-500/50 transition-all">
                <div class="w-12 h-12 bg-blue-500/10 rounded-2xl flex items-center justify-center mb-6 text-blue-500">
                    <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/></svg>
                </div>
                <h3 class="text-xl font-bold mb-3">Real-time Analytics</h3>
                <p class="text-zinc-500 text-sm leading-relaxed">Integrated event tracking with a high-density management dashboard.</p>
            </div>
        </div>
    </main>

    <footer class="mt-40 border-t border-white/5 py-12 px-8 flex justify-between items-center text-[10px] font-mono uppercase tracking-[0.3em] text-zinc-700">
        <div>© 2026 Alpha Labs Inc.</div>
        <div>System Level: Optimal</div>
    </footer>
</body>
</html>`
    },
    icon: LayoutTemplate,
    color: 'text-blue-400',
    bgColor: 'bg-blue-400/10'
  },
  {
    id: 'item-2',
    title: 'Venture_CRM Protocol',
    description: 'A modern relationship management engine for VCs and high-velocity deal teams. Track pipelines, manage LPs, and automate outreach.',
    category: 'System',
    price: -1, // Pro
    author: 'Yacht Labs',
    metrics: { installs: 1120, revenue: 0, engagement: 95 },
    files: {
      'index.html': `<!DOCTYPE html>
<html>
<head>
    <script src="https://cdn.tailwindcss.com"></script>
    <link href="https://fonts.googleapis.com/css2?family=JetBrains+Mono&display=swap" rel="stylesheet">
    <style>body { font-family: 'Inter', sans-serif; }</style>
</head>
<body class="bg-[#050505] text-white flex h-screen overflow-hidden">
    <!-- Sidebar -->
    <aside class="w-64 border-r border-white/10 p-6 flex flex-col gap-8 bg-zinc-950">
        <div class="flex items-center gap-2 px-2">
            <div class="w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center text-black font-bold text-[10px]">V</div>
            <span class="font-mono text-sm tracking-tighter uppercase font-bold">Venture_CRM</span>
        </div>
        <nav class="flex-1 space-y-1">
            <div class="p-3 bg-white/5 rounded-xl flex items-center gap-3 text-emerald-400 font-mono text-[10px] uppercase tracking-widest border border-emerald-500/20">
                <div class="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse"></div>
                Pipeline
            </div>
            <div class="p-3 text-zinc-600 hover:text-white transition-colors font-mono text-[10px] uppercase tracking-widest">Portfolio</div>
            <div class="p-3 text-zinc-600 hover:text-white transition-colors font-mono text-[10px] uppercase tracking-widest">Outreach</div>
            <div class="p-3 text-zinc-600 hover:text-white transition-colors font-mono text-[10px] uppercase tracking-widest">Admin</div>
        </nav>
    </aside>

    <!-- Main Content -->
    <main class="flex-1 flex flex-col min-w-0 bg-black">
        <header class="h-16 border-b border-white/10 px-8 flex items-center justify-between">
            <h2 class="font-mono text-xs uppercase tracking-widest text-zinc-500">Pipeline Stage: Series A</h2>
            <button class="px-4 py-2 bg-emerald-600 text-black font-bold rounded-lg text-[10px] font-mono uppercase tracking-widest hover:bg-emerald-500">Add Deal</button>
        </header>
        
        <div class="p-8 grid grid-cols-4 gap-6 overflow-y-auto">
            <!-- Deal Card -->
            <div class="bg-zinc-900/50 p-6 rounded-2xl border border-white/5 hover:border-emerald-500/30 transition-all group">
                <div class="flex justify-between items-start mb-6">
                    <div class="p-2 bg-white/5 rounded-lg border border-white/10 group-hover:border-emerald-500/20">🚀</div>
                    <span class="text-[8px] font-mono text-emerald-500 uppercase px-2 py-0.5 bg-emerald-500/10 rounded-full">Due Diligence</span>
                </div>
                <h4 class="font-bold text-lg mb-1">Nebula Health</h4>
                <p class="text-zinc-500 text-xs mb-4">AI-first surgical diagnostics platform.</p>
                <div class="flex justify-between items-center text-[10px] font-mono text-zinc-600 uppercase">
                    <span>$2.4M Round</span>
                    <span>5d ago</span>
                </div>
            </div>
            <!-- Repeat Deal Cards -->
        </div>
    </main>
</body>
</html>`
    },
    icon: Cpu,
    color: 'text-emerald-400',
    bgColor: 'bg-emerald-400/10'
  },
  {
    id: 'item-3',
    title: 'Kinetic_Supply Protocol',
    description: 'High-density supply chain tracking and logistics dashboard. Real-time fleet kinetics with acoustic alert synthesis.',
    category: 'System',
    price: 19.0,
    author: 'Yacht Labs',
    metrics: { installs: 430, revenue: 8170, engagement: 91 },
    files: {
      'index.html': `<!DOCTYPE html>
<html>
<head>
    <script src="https://cdn.tailwindcss.com"></script>
    <link href="https://fonts.googleapis.com/css2?family=JetBrains+Mono&display=swap" rel="stylesheet">
</head>
<body class="bg-black text-white p-8 font-sans overflow-hidden">
    <div class="max-w-7xl mx-auto space-y-8">
        <div class="flex justify-between items-end">
            <div class="space-y-2">
                <div class="flex items-center gap-2">
                    <div class="w-1.5 h-1.5 bg-orange-500 rounded-full animate-ping"></div>
                    <span class="text-[10px] font-mono text-orange-500 uppercase tracking-[0.3em]">Network Active</span>
                </div>
                <h1 class="text-5xl font-bold tracking-tighter uppercase">Kinetic_Grid</h1>
            </div>
            <div class="text-right">
                <div class="text-zinc-600 font-mono text-xs uppercase tracking-widest mb-1">Fleet Status</div>
                <div class="text-3xl font-bold tracking-tighter">98.2<span class="text-zinc-700 text-lg ml-1">%</span></div>
            </div>
        </div>

        <div class="grid grid-cols-4 gap-8">
            <div class="col-span-3 aspect-[21/9] bg-zinc-900 border border-white/5 rounded-[40px] relative overflow-hidden flex items-center justify-center">
                <div class="absolute inset-0 bg-[url('https://picsum.photos/seed/map/1200/500')] opacity-20 grayscale brightness-50"></div>
                <!-- Logic Visualizers -->
                <div class="relative z-10 w-full px-12 flex justify-between gap-12">
                   <div class="flex-1 h-32 bg-orange-500/5 border border-orange-500/20 rounded-3xl backdrop-blur-md p-6">
                       <div class="text-[8px] font-mono text-orange-500 uppercase mb-2">Transit Units</div>
                       <div class="text-3xl font-bold italic">14,289</div>
                   </div>
                   <div class="flex-1 h-32 bg-blue-500/5 border border-blue-500/20 rounded-3xl backdrop-blur-md p-6">
                       <div class="text-[8px] font-mono text-blue-500 uppercase mb-2">Efficiency Rating</div>
                       <div class="text-3xl font-bold italic">86.4%</div>
                   </div>
                </div>
            </div>

            <div class="space-y-4">
                <div class="p-6 bg-zinc-900/50 border border-white/5 rounded-3xl">
                    <h3 class="font-mono text-[10px] uppercase text-zinc-500 mb-4">Urgent Log</h3>
                    <div class="space-y-2">
                        <div class="text-[9px] font-mono p-2 bg-red-500/10 text-red-500 border border-red-500/20 rounded">UNIT_401 Slowing in Sector 4</div>
                        <div class="text-[9px] font-mono p-2 bg-yellow-500/10 text-yellow-500 border border-yellow-500/20 rounded">Arrival delay: Berlin Hub</div>
                        <div class="text-[9px] font-mono p-2 bg-zinc-800 text-zinc-500 border border-white/5 rounded opacity-50">UNIT_102 docked safely</div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</body>
</html>`
    },
    icon: Layers,
    color: 'text-orange-400',
    bgColor: 'bg-orange-400/10'
  },
  {
    id: 'item-4',
    title: 'Neural_Support Bot',
    description: 'An autonomous customer support agent powered by Gemini. Resolves complex technical inquiries and manages multi-thread escalations.',
    category: 'AI Workflow',
    price: -1, // Pro
    author: 'Yacht Labs',
    metrics: { installs: 2850, revenue: 0, engagement: 99 },
    files: {
      'index.html': `<!DOCTYPE html>
<html>
<head><script src="https://cdn.tailwindcss.com"></script></head>
<body class="bg-[#050505] text-white flex items-center justify-center min-h-screen p-8">
    <div class="max-w-2xl w-full bg-zinc-900/50 border border-white/10 rounded-[48px] p-12 relative overflow-hidden">
        <div class="absolute top-0 right-0 p-12 opacity-5 pointer-events-none">
            <svg class="w-64 h-64" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clip-rule="evenodd"></path></svg>
        </div>
        
        <div class="relative z-10 space-y-12">
            <div class="flex items-center gap-6">
                <div class="w-20 h-20 bg-blue-600 rounded-[32px] flex items-center justify-center shadow-xl shadow-blue-600/20">
                    <svg class="w-10 h-10 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"></path></svg>
                </div>
                <div>
                    <h2 class="text-4xl font-bold tracking-tighter mb-1">Neural_Support v4</h2>
                    <p class="text-blue-500 font-mono text-xs uppercase tracking-widest">Autonomous Logical Agent</p>
                </div>
            </div>

            <div class="grid grid-cols-2 gap-8">
                <div class="space-y-2">
                    <div class="text-[10px] font-mono text-zinc-500 uppercase tracking-widest">Active Solutions Today</div>
                    <div class="text-5xl font-bold tracking-tighter">1,204</div>
                </div>
                <div class="space-y-2">
                    <div class="text-[10px] font-mono text-zinc-500 uppercase tracking-widest">Accuracy Rating</div>
                    <div class="text-5xl font-bold tracking-tighter">99.8<span class="text-zinc-700 text-2xl">%</span></div>
                </div>
            </div>

            <div class="pt-12 border-t border-white/5 flex gap-4">
               <div class="px-5 py-3 bg-white/5 border border-white/10 rounded-2xl text-[10px] font-mono text-zinc-400 uppercase tracking-widest">Multi-lingual</div>
               <div class="px-5 py-3 bg-white/5 border border-white/10 rounded-2xl text-[10px] font-mono text-zinc-400 uppercase tracking-widest">Sentiment Aware</div>
               <div class="px-5 py-3 bg-white/5 border border-white/10 rounded-2xl text-[10px] font-mono text-zinc-400 uppercase tracking-widest">Self-Improving</div>
            </div>

            <button class="w-full h-16 bg-white text-black font-bold text-sm font-mono uppercase tracking-[0.3em] rounded-3xl hover:bg-zinc-200 transition-all hover:scale-[0.98]">Activate Interface</button>
        </div>
    </div>
</body>
</html>`
    },
    icon: Cpu,
    color: 'text-blue-400',
    bgColor: 'bg-blue-400/10'
  },
  {
    id: 'item-5',
    title: 'Aesthetics_Studio Portfolio',
    description: 'An ultra-minimalist, editorial-grade portfolio for creative professionals. Features smooth locomotion, high-fidelity image optimizations, and a custom typography system.',
    category: 'UI Kit',
    price: 0,
    author: 'Yacht Labs',
    metrics: { installs: 2150, revenue: 0, engagement: 97 },
    files: {
      'index.html': `<!DOCTYPE html>
<html lang="en">
<head>
    <script src="https://cdn.tailwindcss.com"></script>
    <link href="https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;700&display=swap" rel="stylesheet">
    <style>body { font-family: 'Outfit', sans-serif; }</style>
</head>
<body class="bg-white text-black scroll-smooth">
    <div class="fixed top-0 left-0 w-full h-1 bg-black z-[100] origin-left scale-x-0 transition-transform duration-500 hover:scale-x-100"></div>
    
    <header class="h-screen flex flex-col justify-center px-12 md:px-24">
        <nav class="absolute top-12 left-0 w-full px-12 md:px-24 flex justify-between items-center text-[10px] font-bold uppercase tracking-widest">
            <div>The Agency / 2026</div>
            <div class="flex gap-8">
                <a href="#" class="hover:line-through transition-all">Work</a>
                <a href="#" class="hover:line-through transition-all">About</a>
                <a href="#" class="hover:line-through transition-all">Journal</a>
            </div>
        </nav>
        <h1 class="text-[12vw] font-bold tracking-tighter leading-[0.85] uppercase">Pure<br/>Visuals.</h1>
        <div class="mt-12 flex justify-between items-end">
            <p class="max-w-xs text-sm leading-relaxed">Designing digital experiences for brands that prioritize aesthetic integrity and structural performance.</p>
            <div class="text-[10px] font-bold uppercase tracking-widest border-b-2 border-black pb-1 cursor-pointer">Explore Archive ↓</div>
        </div>
    </header>

    <div class="grid grid-cols-1 md:grid-cols-2 gap-1 px-1 mt-24">
        <div class="aspect-square bg-zinc-100 group overflow-hidden relative">
            <img src="https://picsum.photos/seed/design1/800/800" class="w-full h-full object-cover grayscale brightness-95 group-hover:grayscale-0 transition-all duration-700 hover:scale-110" />
            <div class="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                <span class="text-white font-bold uppercase tracking-widest text-xs">Project Alpha</span>
            </div>
        </div>
        <div class="aspect-square bg-zinc-200 group overflow-hidden relative">
             <img src="https://picsum.photos/seed/design2/800/800" class="w-full h-full object-cover grayscale brightness-95 group-hover:grayscale-0 transition-all duration-700 hover:scale-110" />
             <div class="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                <span class="text-white font-bold uppercase tracking-widest text-xs">Project Beta</span>
            </div>
        </div>
    </div>
</body>
</html>`
    },
    icon: LayoutTemplate,
    color: 'text-zinc-600',
    bgColor: 'bg-zinc-600/10'
  },
  {
    id: 'item-6',
    title: 'Cloud_Sentry Monitor',
    description: 'Enterprise-grade infrastructure monitoring dashboard. Real-time telemetry, anomaly detection, and automated incident response workflows.',
    category: 'System',
    price: -1, // Pro
    author: 'Yacht Labs',
    metrics: { installs: 890, revenue: 0, engagement: 99 },
    files: {
      'index.html': `<!DOCTYPE html>
<html>
<head>
    <script src="https://cdn.tailwindcss.com"></script>
    <link href="https://fonts.googleapis.com/css2?family=JetBrains+Mono&display=swap" rel="stylesheet">
</head>
<body class="bg-[#020202] text-zinc-300 font-mono text-[10px] p-6 lg:p-12 overflow-hidden h-screen flex flex-col">
    <div class="flex justify-between items-center mb-12 shrink-0">
        <div class="flex items-center gap-4">
            <div class="w-10 h-10 bg-[#FF0055] rounded-xl flex items-center justify-center text-white shadow-[0_0_30px_rgba(255,0,85,0.4)]">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"/></svg>
            </div>
            <div>
                <h1 class="text-xl font-bold tracking-widest text-white uppercase">Cloud_Sentry</h1>
                <p class="text-zinc-600 uppercase tracking-[0.3em] text-[8px]">Global Deployment Node: HK-01</p>
            </div>
        </div>
        <div class="flex gap-8">
            <div class="text-right">
                <p class="text-zinc-600 uppercase mb-1">Response Time</p>
                <p class="text-white text-lg font-bold">14<span class="text-[10px] ml-1">ms</span></p>
            </div>
            <div class="text-right">
                <p class="text-zinc-600 uppercase mb-1">Health Score</p>
                <p class="text-emerald-500 text-lg font-bold">99.2%</p>
            </div>
        </div>
    </div>

    <div class="flex-1 grid grid-cols-6 grid-rows-6 gap-6">
        <div class="col-span-4 row-span-4 bg-zinc-900/30 border border-zinc-800 rounded-3xl p-8 flex flex-col overflow-hidden">
            <div class="flex justify-between items-center mb-8">
                <h2 class="text-sm font-bold uppercase tracking-widest text-white">Global Telemetry</h2>
                <div class="flex gap-2">
                    <div class="px-3 py-1 bg-zinc-800 rounded text-zinc-400">Hour</div>
                    <div class="px-3 py-1 bg-white/10 text-white rounded">Live</div>
                </div>
            </div>
            <div class="flex-1 border-b border-zinc-800/50 flex items-end gap-1 pb-4">
                ${Array.from({length: 40}).map(() => `
                    <div class="flex-1 bg-[#FF0055]/30 hover:bg-[#FF0055] transition-all cursor-pointer" style="height: ${Math.random() * 100}%"></div>
                `).join('')}
            </div>
        </div>
        <div class="col-span-2 row-span-2 bg-[#FF0055]/5 border border-[#FF0055]/20 rounded-3xl p-8">
            <h2 class="text-sm font-bold uppercase tracking-widest text-[#FF0055] mb-4">Anomalies Detected</h2>
            <div class="space-y-3">
                <div class="flex justify-between border-b border-white/5 pb-2">
                    <span class="text-zinc-500">Latency Spike</span>
                    <span class="text-white">US-EAST-1</span>
                </div>
                <div class="flex justify-between border-b border-white/5 pb-2">
                    <span class="text-zinc-500">CPU Overhead</span>
                    <span class="text-white">DB-CLUSTER-04</span>
                </div>
            </div>
        </div>
        <div class="col-span-2 row-span-4 bg-zinc-900/50 border border-zinc-800 rounded-3xl p-8">
            <h2 class="text-sm font-bold uppercase tracking-widest text-white mb-6">Incident Logs</h2>
            <div class="space-y-4 font-mono text-[9px] opacity-60">
                <p>> Authenticating session... [OK]</p>
                <p>> Fetching node status... [HK-01, SG-02]</p>
                <p class="text-[#FF0055]">> WARNING: Ingress bypass detected on /api/v2</p>
                <p>> Initiating sub-protocol firewall...</p>
                <p class="text-emerald-500">> Threat neutralized. System stable.</p>
            </div>
        </div>
    </div>
</body>
</html>`
    },
    icon: ShieldCheck,
    color: 'text-pink-500',
    bgColor: 'bg-pink-500/10'
  }
];

export default function Marketplace({ profile, setLastBuild }: { profile?: any, setLastBuild?: any }) {
  const [items, setItems] = useState<any[]>(DEFAULT_MARKETPLACE_ITEMS);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState<any | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const [isDeploying, setIsDeploying] = useState(false);
  const navigate = useNavigate();

  const categories = ['All', 'Template', 'AI Workflow', 'UI Kit', 'Growth System', 'System'];

  useEffect(() => {
    const fetchItems = async () => {
      try {
        const q = query(collection(db, 'marketplace'), orderBy('createdAt', 'desc'));
        const snapshot = await getDocs(q);
        if (!snapshot.empty) {
          const fetchedItems = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          setItems(fetchedItems);
        }
      } catch (error) {
        console.error('Error fetching marketplace items:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchItems();
  }, []);

  const filteredItems = items.filter(item => {
    const matchesSearch = item.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
                          item.description.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = activeCategory === 'All' || item.category === activeCategory;
    return matchesSearch && matchesCategory;
  });

  const handleDeploy = async (item: any) => {
    if (!auth.currentUser) {
      toast.error('Please sign in to deploy this item.');
      navigate('/#auth');
      return;
    }

    if (item.price > 0 && !profile?.isPro) {
      toast.info('Stripe integration required for paid items. (Mocking success)');
      // In a real app, redirect to Stripe Checkout here
    } else if (item.price === -1 && !profile?.isPro) {
      toast.error('This item requires a Pro subscription.');
      return;
    }

    setIsDeploying(true);
    try {
      // 1. Create a new project
      const projectRef = await addDoc(collection(db, 'projects'), {
        name: item.title,
        description: `Deployed from Marketplace: ${item.description}`,
        ownerId: auth.currentUser.uid,
        autopilot: false,
        isLaunched: false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      // 2. Create documents for each file in the template
      if (item.files) {
        for (const [filename, content] of Object.entries(item.files)) {
          await addDoc(collection(db, `projects/${projectRef.id}/documents`), {
            title: filename,
            content: content,
            projectId: projectRef.id,
            ownerId: auth.currentUser.uid,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp()
          });
        }
      } else {
        // Fallback if no files defined
        await addDoc(collection(db, `projects/${projectRef.id}/documents`), {
          title: 'Architecture.md',
          content: `# ${item.title}\n\nDeployed from Marketplace.`,
          projectId: projectRef.id,
          ownerId: auth.currentUser.uid,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });
      }

      if (setLastBuild) {
        setLastBuild({
          brief: `Template: ${item.title}`,
          files: item.files ? Object.entries(item.files).map(([name, content]: [string, any]) => ({ name, content })) : [{ name: 'Architecture.md', content: `# ${item.title}\n\nDeployed from Marketplace.` }],
          docId: null
        });
      }

      toast.success(`${item.title} deployed successfully!`);
      setSelectedItem(null);
      navigate(`/studio/edit?project=${projectRef.id}`);
    } catch (error: any) {
      console.error('Deployment error:', error);
      toast.error(`Failed to deploy: ${error.message}`);
    } finally {
      setIsDeploying(false);
    }
  };

  const getPriceDisplay = (price: number) => {
    if (price === 0) return <span className="text-emerald-400 font-mono text-xs uppercase tracking-widest">Free</span>;
    if (price === -1) return <span className="text-purple-400 font-mono text-xs uppercase tracking-widest flex items-center gap-1"><Zap size={10} /> Pro</span>;
    return <span className={`font-mono text-xs uppercase tracking-widest ${profile?.isPro ? 'text-[#555555] line-through' : 'text-white'}`}>${price}</span>;
  };

  return (
    <div className={`min-h-screen bg-[#050505] text-white ${!profile ? 'pt-24' : ''}`}>
      {!profile && <Navbar />}

      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Header Section */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-4">
            <Store className="text-[#0074D9]" size={32} />
            <h1 className="text-4xl font-mono uppercase tracking-tighter">Marketplace</h1>
          </div>
          <p className="text-[#888888] font-light max-w-2xl text-lg">
            Discover high-quality, ready-to-use project templates, growth systems, and AI workflows. 
            Deploy them instantly into your workspace.
          </p>
        </div>

        {/* Filters & Search */}
        <div className="flex flex-col md:flex-row gap-4 mb-12 items-center justify-between bg-[#0A0A0A] p-4 rounded-xl border border-[#222222]">
          <div className="flex items-center gap-2 overflow-x-auto w-full md:w-auto pb-2 md:pb-0 hide-scrollbar">
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-4 py-2 rounded-lg font-mono text-xs uppercase tracking-widest whitespace-nowrap transition-all ${
                  activeCategory === cat 
                    ? 'bg-[#111111] text-white border border-[#333333]' 
                    : 'text-[#888888] hover:text-white border border-transparent hover:bg-[#111111]'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
          <div className="relative w-full md:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#555555]" size={16} />
            <input 
              type="text"
              placeholder="Search assets..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-[#111111] border border-[#222222] rounded-lg py-2 pl-10 pr-4 text-sm text-white placeholder:text-[#555555] focus:outline-none focus:border-[#0074D9] transition-colors font-mono"
            />
          </div>
        </div>

        {/* Grid */}
        {loading ? (
          <div className="flex justify-center py-32">
            <div className="w-8 h-8 border-2 border-[#333333] border-t-[#0074D9] rounded-full animate-spin" />
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredItems.map((item, index) => {
              const Icon = item.icon || Layers;
              return (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.05 }}
                  onClick={() => setSelectedItem(item)}
                  className="group bg-[#0A0A0A] border border-[#222222] rounded-xl overflow-hidden cursor-pointer hover:border-[#0074D9]/50 hover:shadow-[0_0_20px_rgba(0,116,217,0.05)] transition-all flex flex-col h-full"
                >
                  {/* Preview Area */}
                  <div className="aspect-video bg-black border-b border-[#222222] relative group-hover:bg-[#050505] transition-colors overflow-hidden">
                    {item.files?.['index.html'] ? (
                        <iframe 
                            srcDoc={item.files['index.html']}
                            className="absolute top-0 left-0 w-[400%] h-[400%] origin-top-left scale-[0.25] pointer-events-none opacity-40 group-hover:opacity-100 transition-opacity"
                            tabIndex={-1}
                            title={item.title}
                        />
                    ) : (
                        <div className="absolute inset-0 flex items-center justify-center opacity-20">
                            <Icon size={48} className={item.color} />
                        </div>
                    )}
                    <div className="absolute inset-0 z-10" />
                  </div>

                  <div className="p-6 flex-1">
                    <div className="flex items-start justify-between mb-4">
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${item.bgColor || 'bg-[#111111]'}`}>
                        <Icon className={item.color || 'text-white'} size={24} />
                      </div>
                      <div className="bg-[#111111] border border-[#222222] px-3 py-1 rounded-full">
                        {getPriceDisplay(item.price)}
                      </div>
                    </div>
                    <h3 className="text-xl font-mono uppercase tracking-tight mb-2 group-hover:text-[#0074D9] transition-colors">{item.title}</h3>
                    <p className="text-[#888888] text-sm font-light line-clamp-3 mb-4">{item.description}</p>
                  </div>
                  <div className="px-6 py-4 border-t border-[#222222] bg-[#080808] flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-1.5 text-[#555555] text-xs font-mono">
                        <Download size={14} />
                        {item.metrics?.installs.toLocaleString() || 0}
                      </div>
                      <div className="flex items-center gap-1.5 text-[#555555] text-xs font-mono">
                        <Star size={14} />
                        {item.metrics?.engagement || 0}%
                      </div>
                    </div>
                    <span className="text-xs font-mono uppercase tracking-widest text-[#555555]">{item.category}</span>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}

        {filteredItems.length === 0 && !loading && (
          <div className="text-center py-32 border border-dashed border-[#222222] rounded-xl">
            <Search className="mx-auto text-[#333333] mb-4" size={48} />
            <h3 className="text-xl font-mono uppercase tracking-tight text-[#888888] mb-2">No items found</h3>
            <p className="text-[#555555] font-light">Try adjusting your search or filters.</p>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      <AnimatePresence>
        {selectedItem && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-6">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedItem(null)}
              className="absolute inset-0 bg-black/80 backdrop-blur-sm"
            />
            <motion.div 
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative w-full max-w-4xl max-h-[90vh] bg-[#0A0A0A] border border-[#222222] rounded-2xl overflow-hidden shadow-2xl flex flex-col md:flex-row"
            >
              {/* Left Column: Info */}
              <div className="flex-1 p-8 overflow-y-auto border-r border-[#222222]">
                <button 
                  onClick={() => setSelectedItem(null)}
                  className="absolute top-4 right-4 md:hidden text-[#555555] hover:text-white"
                >
                  <X size={24} />
                </button>
                
                <div className="flex items-center gap-3 mb-6">
                  <span className="px-3 py-1 bg-[#111111] border border-[#222222] rounded-full text-xs font-mono uppercase tracking-widest text-[#888888]">
                    {selectedItem.category}
                  </span>
                  <span className="text-xs font-mono text-[#555555]">by {selectedItem.author}</span>
                </div>

                <h2 className="text-3xl font-mono uppercase tracking-tighter mb-4">{selectedItem.title}</h2>
                <p className="text-[#888888] font-light text-lg mb-8 leading-relaxed">
                  {selectedItem.description}
                </p>

                <div className="space-y-6">
                  <div>
                    <h4 className="text-xs font-mono uppercase tracking-widest text-[#555555] mb-3">What's Included</h4>
                    <ul className="space-y-2">
                      {selectedItem.files && Object.keys(selectedItem.files).map(file => (
                        <li key={file} className="flex items-center gap-2 text-sm text-[#cccccc] font-mono bg-[#111111] p-2 rounded-lg border border-[#222222]">
                          <Code2 size={16} className="text-[#0074D9]" />
                          {file}
                        </li>
                      ))}
                      {!selectedItem.files && (
                        <li className="flex items-center gap-2 text-sm text-[#cccccc] font-mono bg-[#111111] p-2 rounded-lg border border-[#222222]">
                          <LayoutTemplate size={16} className="text-[#0074D9]" />
                          Full Project Architecture
                        </li>
                      )}
                    </ul>
                  </div>

                  <div>
                    <h4 className="text-xs font-mono uppercase tracking-widest text-[#555555] mb-3">Social Proof</h4>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="bg-[#111111] border border-[#222222] p-4 rounded-xl text-center">
                        <div className="text-2xl font-mono text-white mb-1">{selectedItem.metrics?.installs.toLocaleString() || 0}</div>
                        <div className="text-[10px] font-mono uppercase tracking-widest text-[#555555]">Installs</div>
                      </div>
                      <div className="bg-[#111111] border border-[#222222] p-4 rounded-xl text-center">
                        <div className="text-2xl font-mono text-white mb-1">{selectedItem.metrics?.engagement || 0}%</div>
                        <div className="text-[10px] font-mono uppercase tracking-widest text-[#555555]">Engagement</div>
                      </div>
                      <div className="bg-[#111111] border border-[#222222] p-4 rounded-xl text-center">
                        <div className="text-2xl font-mono text-emerald-400 mb-1">
                          ${selectedItem.metrics?.revenue >= 1000 ? `${(selectedItem.metrics.revenue / 1000).toFixed(1)}k` : selectedItem.metrics?.revenue || 0}
                        </div>
                        <div className="text-[10px] font-mono uppercase tracking-widest text-[#555555]">Gen. Rev</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column: Action */}
              <div className="w-full md:w-80 bg-[#080808] p-8 flex flex-col relative">
                <button 
                  onClick={() => setSelectedItem(null)}
                  className="absolute top-4 right-4 hidden md:block text-[#555555] hover:text-white transition-colors"
                >
                  <X size={24} />
                </button>

                <div className="flex-1 flex flex-col justify-center">
                  <div className="text-center mb-8">
                    <div className="text-sm font-mono uppercase tracking-widest text-[#555555] mb-2">Price</div>
                    <div className="text-4xl font-mono tracking-tighter">
                      {selectedItem.price === 0 ? 'Free' : selectedItem.price === -1 ? 'Pro Only' : `$${selectedItem.price}`}
                    </div>
                  </div>

                  <button
                    onClick={() => handleDeploy(selectedItem)}
                    disabled={isDeploying}
                    className={`w-full py-4 rounded-xl font-mono text-sm uppercase tracking-widest font-bold flex items-center justify-center gap-2 transition-all ${
                      isDeploying 
                        ? 'bg-[#333333] text-[#888888] cursor-not-allowed'
                        : selectedItem.price === -1 && !profile?.isPro
                          ? 'bg-purple-500/10 text-purple-400 border border-purple-500/20 hover:bg-purple-500/20'
                          : 'bg-white text-black hover:bg-zinc-200 hover:scale-[1.02]'
                    }`}
                  >
                    {isDeploying ? (
                      <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                    ) : selectedItem.price === -1 && !profile?.isPro ? (
                      <>
                        <Lock size={18} />
                        Unlock with Pro
                      </>
                    ) : (
                      <>
                        <Zap size={18} />
                        Deploy to Build
                      </>
                    )}
                  </button>

                  <div className="mt-6 space-y-3">
                    <div className="flex items-center gap-2 text-xs font-mono text-[#888888]">
                      <CheckCircle size={14} className="text-emerald-400" />
                      One-click deployment
                    </div>
                    <div className="flex items-center gap-2 text-xs font-mono text-[#888888]">
                      <CheckCircle size={14} className="text-emerald-400" />
                      Full source code access
                    </div>
                    <div className="flex items-center gap-2 text-xs font-mono text-[#888888]">
                      <CheckCircle size={14} className="text-emerald-400" />
                      Pre-configured architecture
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
}
